import { DateTime } from "luxon";
import { BuildServices, GenericRouteEvent, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap } from "./generic-route-event.model";
import { Route } from "src/app/types/route.type";
import * as helpers from '../../helpers/ui-helpers';
import { BaseJob } from "../job.type";
import { Capacitor } from "@capacitor/core";

export class PullOut extends GenericRouteEvent {

  public type = 'pull-out';
  value: BaseJob;

  constructor(route: Route, job: BaseJob, services: BuildServices) {
    super(services);
    const { scheduledStart, pullOutEarlinessLimit: limit } = route;
    this.completed = !!job.arriveTime;
    this.scheduledStart = DateTime.fromISO(job.scheduledTime ?? scheduledStart?.toDateString() ?? job.estimatedTimeOfArrival);
    this.value = job;
    this.headerInfo = {
      time: helpers.getTimeForCardHeader({ start: this.scheduledStart, end: this.scheduledEnd, translateSvc: services.translateSvc }),
      type: 'LABEL.pullOut',
      details: '',
      timeTo: this.scheduledStart.toRelative(),
    };
    this.stopSequence = job.sequence;
    this.location = job.location;
    this.updateTimeBasedProperties();
  }

  public updateTimeBasedProperties = () => {
    this.isLate = this.completed ? false : helpers.isInThePast(this.scheduledStart);
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = this.getActions();
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative({ unit: [ 'hours', 'minutes'  ] });
  };

  public getActions(): RouteEventAction[] {
    const actions: RouteEventAction[] =  [
      {
        show: !this.completed,
        type: RouteEventActionTypeMap.perform,
        label: this.translateSvc.translate('LABEL.pullOut'),
        icon: '',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.perform(action),
      },
      {
        type: RouteEventActionTypeMap.navigate,
        label: `ACTION.navigate`,
        show: this.value.jobEnabled && helpers.hasValidGeo(this.location),
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: true,
        disabled: false,
        fn: async () => this.locationSvc.navigateToDestination(this.location.coordinate),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios') && helpers.hasValidGeo(this.location),
        icon: 'map', color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        preview: true,
        drivingMode: false,
        disabled: false,
        fn: async () => this.locationSvc.previewLocation(this.location.coordinate, this.location.name),
      },
    ].filter(action => action.show);
    return actions;
  }

  async perform(action: RouteEventAction): Promise<void> {
    try {
      action.disabled = true;
      const confirmedOdo = await helpers.presentOdometer(this.modalController);
      if (!confirmedOdo) { return; }
      this.value.completed = true;
      this.value.arriveTime = Date.now().toString();
      await this.dispatchSvc.arriveJobs([ this ]);
      this.completed = true;
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      action.disabled = false;
    }
  }
}
